package com.devi3east.bashbuddy.footer.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp

@Composable
fun TermsOfServiceScreen(
    onBack: () -> Unit,
    date: String
) {
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
    ) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        Column(
            modifier = contentModifier
                .padding(horizontal = 16.dp)
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(vertical = 8.dp)
            ) {
                IconButton(
                    onClick = onBack,
                    modifier = Modifier.align(Alignment.CenterStart)
                ) {
                    Icon(
                        imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                        contentDescription = "Back",
                        tint = MaterialTheme.colorScheme.onSurface
                    )
                }

                Row(
                    modifier = Modifier.align(Alignment.Center),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = "Terms of Service",
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }
            }

            Text(
                text = "Last updated: $date",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            // Intro & Acceptance
            Text(
                text = "1. Introduction",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = "Welcome to bash buddy™ (the “Platform”, “we”, “us”, or “our”). These Terms of Service (the “Terms”) govern your access and use of the Platform and any related services, websites, or applications provided by bash buddy. By accessing or using the Platform you accept and agree to be bound by these Terms. If you do not agree, do not use the Platform.",
                style = MaterialTheme.typography.bodyMedium
            )

            // Scope & Role clarification
            Text(
                text = "2. Platform Role & Important Clarifications",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("bash buddy is a technology platform that provides organizations a place to publish fundraising events and to offer tickets for sale online. bash buddy itself does NOT host, run, or operate raffles, lotteries, or prize draws. bash buddy:\n\n")
                    append("- is a software platform that enables qualifying organizations to list events and sell tickets online.\n")
                    append("- does NOT accept payments, hold funds, or process ticket payments directly. Payment processing is handled by third-party payment providers.\n")
                    append("- does NOT host or operate any lottery, raffle, or gambling activity — the organization (the event host) is solely responsible for running any raffle or prize process in compliance with applicable local laws.\n\n")
                    append("By using the Platform you acknowledge and agree that bash buddy's role is limited to providing the online tools and infrastructure for organizations and users to interact. bash buddy does not guarantee the legality, fairness, or outcome of any event, raffle, or prize.")
                },
                style = MaterialTheme.typography.bodyMedium
            )

            // Eligibility & Organization Responsibilities
            Text(
                text = "3. Eligibility; Organization Responsibilities",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("a) Eligibility: To use certain features (such as selling tickets), organizations must meet eligibility criteria and represent that they are legally permitted to host the fundraising activity under applicable law. Individuals and organizations using the Platform must comply with local laws, age restrictions, and any other legal requirements.\n\n")
                    append("b) Organization obligations: Organizations that list events and offer tickets are solely responsible for:\n")
                    append("- Ensuring the event, prizes, and ticket sales are lawful in the jurisdictions where they operate and where ticket buyers are located.\n")
                    append("- Complying with rules, permits, licenses, or approvals required to run raffles, prize draws or fundraising activities.\n")
                    append("- Establishing terms for prize eligibility, winner selection, prize delivery, refunds, cancellations, and disputes.\n")
                    append("- Handling all communications with ticket purchasers and winners concerning their event, tickets, prizes, or refunds.\n\n")
                    append("bash buddy may require proof of authority, documentation, or verification from organizations before enabling ticket sales or other features.")
                },
                style = MaterialTheme.typography.bodyMedium
            )

            // Payments and Financials
            Text(
                text = "4. Payments, Payment Information & Financial Responsibility",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("- Third-party payment processors: Transactions initiated via the Platform are processed by third-party payment providers. bash buddy does not accept or store payment card numbers or direct payment information for ticket purchases unless explicitly stated and implemented via a third-party integration. Always review the payment provider’s privacy and terms.\n\n")
                    append("- No custody of funds: bash buddy does NOT hold ticket buyer funds for events; organizations are responsible for configuring and managing payment collection and reconciliation.\n\n")
                    append("- Refunds and disputes: Refunds, cancellations, and disputes are managed by the organization in accordance with the organization’s policies and any agreement with the payment processor. bash buddy may provide tools to assist but is not responsible for refund decisions unless we explicitly agree otherwise in writing.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Data & Privacy summary
            Text(
                text = "5. Privacy & Data Handling (Summary)",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("- We collect and process certain data to operate the Platform, to enable event listings, ticket sales, and user accounts. Our Privacy Policy (available separately) explains the categories of data collected, how that data is used, and how long it is retained.\n\n")
                    append("- Payment details: bash buddy does not store full payment card numbers or payment credentials. Payment credentials are handled by the chosen payment processor. We may store transaction metadata, receipts, or identifiers required for order tracking and customer support.\n\n")
                    append("- No sale of personal data: We do not sell users’ personal data to third parties. We may share data with service providers and partners (payment processors, analytics providers, email providers) to provide the Platform and related services; such disclosures are governed by our Privacy Policy.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // User & Purchaser responsibilities
            Text(
                text = "6. Users and Ticket Purchasers — Responsibilities & Acknowledgements",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("- Purchasers should confirm the organization’s legitimacy, event details, terms and eligibility rules prior to buying tickets.\n")
                    append("- Purchasers are responsible for understanding any age, residency, or regulatory requirements imposed by the organization or law.\n")
                    append("- bash buddy is not responsible for the organization’s compliance with local law, the quality of an event, prize fulfillment, or communication from the organization.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // No gambling or lottery claim
            Text(
                text = "7. No Gambling Service",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("The Platform is not a gambling operator. bash buddy does not run lotteries, sweepstakes, or gambling operations. If an organization uses the Platform to organize an event that might be considered gambling or a lottery under local law, it is the organization's responsibility to ensure compliance and to obtain all required licenses and permissions. bash buddy disclaims any involvement in operating such activities.")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Content and conduct
            Text(
                text = "8. Content, Conduct & Prohibited Use",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("- Users and organizations must not use the Platform to post unlawful, infringing, or offensive content.\n")
                    append("- You agree not to attempt to interfere with the Platform infrastructure, to harvest data, or to reverse-engineer the services.\n")
                    append("- bash buddy may remove content, suspend accounts, or refuse service where we reasonably believe there has been a violation of these Terms or applicable law.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Intellectual property
            Text(
                text = "9. Intellectual Property",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("- The Platform, its content, trademarks and software are owned by bash buddy or its licensors. You may not reproduce, distribute, or create derivative works from our proprietary content without express permission.\n")
                    append("- Organizations retain ownership of their event content and materials, but grant bash buddy a license to display and operate that content as part of the Platform.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Warranties & disclaimers
            Text(
                text = "10. Disclaimers; No Warranties",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("- THE PLATFORM IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, BASH BUDDY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.\n\n")
                    append("- bash buddy does not warrant the legality, accuracy, or quality of events, prizes, or organization communications. ALL PURCHASES ARE MADE BETWEEN PURCHASERS AND ORGANIZATIONS.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Limitation of liability
            Text(
                text = "11. Limitation of Liability",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("TO THE MAXIMUM EXTENT PERMITTED BY LAW, BASH BUDDY AND ITS AFFILIATES SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, OR LOSS OF PROFITS, REVENUE, DATA OR GOODWILL ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM. OUR AGGREGATE LIABILITY IS LIMITED TO THE AMOUNT YOU PAID (IF ANY) FOR ACCESS TO THE PLATFORM IN THE PRIOR TWELVE (12) MONTHS, OR A NOMINAL AMOUNT AS REQUIRED BY LAW.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Indemnification
            Text(
                text = "12. Indemnification",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("You agree to indemnify, defend and hold harmless bash buddy and its officers, directors, employees and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys’ fees and costs) arising out of or in any way connected with your use of the Platform, violation of these Terms, or your breach of any representation or warranty.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Termination & suspension
            Text(
                text = "13. Termination",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("bash buddy may suspend or terminate your access to the Platform at any time for violation of these Terms, suspected illegal activity, or for other reasons. Upon termination, your rights to use the Platform end and we may delete data associated with your account in accordance with our data retention policies.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Changes to terms
            Text(
                text = "14. Changes to These Terms",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("We may modify these Terms from time to time. When we do, we will update the \"Last updated\" date and, where required by law, provide notice. Continued use of the Platform following changes constitutes acceptance of the updated Terms.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Governing law
            Text(
                text = "15. Governing Law & Dispute Resolution",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("These Terms are governed by the laws of [State/Country]. Any dispute relating to these Terms or the Platform shall be resolved in the competent courts located in [Location], unless otherwise agreed in writing. You and bash buddy each submit to the personal jurisdiction of such courts.\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Contact info
            Text(
                text = "16. Contact Us",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = buildString {
                    append("If you have questions about these Terms, or need assistance, please contact us at: \n\n")
                    append("Email: support@bash-buddy.com\n")
                    //("Address: [Company Address]\n\n")
                }, style = MaterialTheme.typography.bodyMedium
            )

            // Entire agreement
            Text(
                text = "17. Entire Agreement",
                style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
            )
            Text(
                text = "These Terms constitute the entire agreement between you and bash buddy regarding the Platform and supersede any prior agreements between you and bash buddy relating to the same subject matter.",
                style = MaterialTheme.typography.bodyMedium
            )

            Spacer(modifier = Modifier.padding(bottom = 48.dp))
        }
    }
}